<script lang="ts">
  import TableHeaderSortable from "@shared/svelte/Components/TableHeaderSortable.svelte";
  import type { PageData } from "@shared/types/PageData";
  import { smartFilter } from "@retailer/svelte/stores/QuickOrderListSmartFilterStore";
  import type { QuickOrderListDetail } from "@shared/types/QuickOrderList";
  import { createEventDispatcher } from "svelte";
  import TrackingContext from "@shared/svelte/Components/Tracking/TrackingContext.svelte";

  import { EventTracking } from "@shared/services/event_tracking/events";

  const { trackEvent, trackClick } = EventTracking;
  export let list: QuickOrderListDetail;
  export let page: PageData;
  export let readOnly: boolean;
  export let editMode: boolean;
  export let showInventory: boolean;
  export let forSculptureRetailer: boolean;
  export let selectedProducts: string[];
  export let removeProducts, toggleSelectAll;
  export let emptyList: boolean;
  export let showPrices: boolean = false;

  const dispatch = createEventDispatcher();

  const handleSortClick = context => {
    return filters => {
      trackClick(`list_sorted_${filters.sort}`, context.entities);
      list.loading = true;
      dispatch("selectFilter", { filters: { ...page.params, ...filters } });
    };
  };

  const headers = {
    productLine: { name: "Product", sortName: "product_line_name", sortable: true },
    productType: { name: "Type", sortName: "product_type_name", sortable: true },
    distributor: { name: "Distributor", sortName: "distributor_name", sortable: true },
    lastOrdered: { name: "Last Ordered", sortName: "last_ordered_at", sortable: true },
    currentPrice: { name: "Current Price", sortName: null, sortable: false },
    quantity: { name: "Qty in Cart", sortName: null, sortable: false },
    onHand: { name: "On Hand", sortName: null, sortable: false },
    par: { name: "Par", sortName: null, sortable: false },
    lastUpdated: { name: "Last Updated", sortName: null, sortable: false },
    used: { name: "Used", sortName: null, sortable: false },
    suggested: { name: "Suggested", sortName: null, sortable: false },
  };

  let headerCheckboxChecked = selectedProducts.length > 0;
  const toggleSelectAllWrapper = function () {
    toggleSelectAll();
    headerCheckboxChecked = selectedProducts.length > 0;
  };

  const removeProductsWrapper = function () {
    list.loading = true;
    return removeProducts();
  };

  $: {
    selectedProducts = selectedProducts;
    headerCheckboxChecked = selectedProducts.length > 0;
  }
</script>

<TrackingContext let:trackingContext>
  <thead>
    <tr>
      {#if !readOnly && !showInventory && !editMode && !emptyList}
        <th style="width: 2%">
          <label class="fizz-flex-row-4" for="header-checkbox">
            {#key headerCheckboxChecked}
              <input
                type="checkbox"
                name="checkbox"
                data-testId="header-checkbox"
                id="header-checkbox"
                class="fizz-checkbox"
                bind:checked={headerCheckboxChecked}
                on:click|preventDefault={toggleSelectAllWrapper}
                value="toggle-select-all"
                style="position: relative; transform: translateY(0%)"
              />
            {/key}
          </label>
        </th>
      {/if}
      {#if selectedProducts.length > 0}
        <th style="padding-block: 12px; width: 25%">
          <div class="fizz-flex-row-24">
            {#if list.loading}
              <strong class="fizz-no-wrap loading"> Loading... </strong>
            {:else}
              <strong class="fizz-no-wrap" data-testId="number-selected-text">
                {selectedProducts.length} selected
              </strong>
              <button
                class="fizz-button-danger fizz-no-wrap fizz-button-small"
                data-testId="delete-button"
                on:click|preventDefault={removeProductsWrapper}
              >
                Remove from list
              </button>
            {/if}
          </div>
        </th>
        <th style="width: 15%" />
        <th style="width: 15%" />
        <th style="width: 15%" />
        <th style="width: 28%" />
      {:else if showInventory}
        <th style="width: 25%">{headers.productLine.name}</th>
        <th style="width: 30%">
          <div class="fizz-flex-row">
            <span class="fizz-flex-item">{headers.onHand.name}</span>
            <span class="fizz-flex-item">{headers.par.name}</span>
          </div>
        </th>
        <th style="width: 12%">{headers.lastUpdated.name}</th>
        <th style="width: 12%">{headers.lastOrdered.name}</th>
        <th style="width: 12%">{headers.currentPrice.name}</th>
        <th>{headers.quantity.name}</th>
      {:else}
        {#if editMode}
          <!-- small TH to line up with the TD for drag handle in edit mode -->
          <th style="width: 2%" />
        {/if}

        {#if forSculptureRetailer}
          <TableHeaderSortable
            handleSortClick={handleSortClick(trackingContext)}
            header={{ ...headers.productLine, width: 25 }}
            {page}
          />
          <th style="width: 10%">{headers.onHand.name}</th>
          <th style="width: 10%">{headers.par.name}</th>
          <th style="width: 10%">{headers.used.name}</th>
          <th style="width: 10%">{headers.suggested.name}</th>
        {:else}
          <TableHeaderSortable
            handleSortClick={handleSortClick(trackingContext)}
            header={{ ...headers.productLine, width: 25 }}
            {page}
          />
          <TableHeaderSortable
            handleSortClick={handleSortClick(trackingContext)}
            header={{ ...headers.productType, width: 15 }}
            {page}
          />
          {#if $smartFilter.distributor}
            <th style="width: 15%">{headers.distributor.name}</th>
          {:else}
            <TableHeaderSortable
              handleSortClick={handleSortClick(trackingContext)}
              header={{ ...headers.distributor, width: 15 }}
              {page}
            />
          {/if}
        {/if}
        <TableHeaderSortable
          handleSortClick={handleSortClick(trackingContext)}
          header={{ ...headers.lastOrdered, width: 15 }}
          {page}
        />
        {#if !editMode}
          {#if showPrices}
            <th>{headers.currentPrice.name}</th>
          {/if}
          <th style="width: 28%">{headers.quantity.name}</th>
        {/if}
      {/if}
    </tr>
  </thead>
</TrackingContext>
